import { useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Page } from "@/components/PublicLayout";
import { useBlocks, useResources, useTrustedProviders } from "@/hooks/useContent";
import { ProviderTags } from "@/pages/staff/StaffReferrals";

const TrustedProviders = ({ block, providers }) => (
  <section className="mt-16 border-t-2 border-olive-900 pt-10" aria-labelledby="trusted-heading" data-testid="trusted-providers">
    <h2 id="trusted-heading" className="text-3xl">{block?.title || "Trusted Providers Beyond Patria"}</h2>
    {block?.body && <p className="mt-3 max-w-[65ch] text-ink-500">{block.body}</p>}
    <ul className="mt-8 grid gap-6 md:grid-cols-2">
      {providers.map((p) => (
        <li key={p.id} className="surface p-5" data-testid={`trusted-provider-${p.id}`}>
          <h3 className="text-xl">{p.name}</h3>
          {p.organization && <p className="text-sm text-ink-500">{p.organization}</p>}
          {p.specialties?.length > 0 && <p className="mt-2 text-sm">{p.specialties.join(", ")}</p>}
          <p className="mt-2 text-sm text-ink-500">{[p.city, p.languages?.length ? p.languages.join(", ") : null].filter(Boolean).join(" · ")}</p>
          <ProviderTags p={p} />
          <div className="mt-3 flex flex-wrap gap-3 text-sm">
            {p.phone && <a href={`tel:${p.phone.replace(/[^\d+]/g, "")}`} className="link" data-testid={`trusted-call-${p.id}`}>Call {p.phone}</a>}
            {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="link inline-flex items-center gap-1">Website <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /></a>}
          </div>
        </li>
      ))}
    </ul>
  </section>
);

export default function ResourcesPage() {
  const { data: blocks } = useBlocks();
  const { data: resources = [] } = useResources();
  const { data: providers = [] } = useTrustedProviders();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const heading = blocks?.["resources.heading"];
  const safety = blocks?.["resources.browsing_safely"];
  const categories = useMemo(() => ["All", ...Array.from(new Set(resources.map((r) => r.category)))], [resources]);
  const filtered = resources.filter((r) => (category === "All" || r.category === category) && (!q || `${r.title} ${r.description}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <Page title={heading?.title || "We're Here To Help"} lede={heading?.body} testId="resources-page">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label htmlFor="resource-search" className="label">Search resources</label>
              <input id="resource-search" className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Marriage, finances, groups…" data-testid="resource-search-input" />
            </div>
            <div>
              <label htmlFor="resource-category" className="label">Category</label>
              <select id="resource-category" className="input sm:w-56" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="resource-category-select">
                {categories.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <ul className="mt-10 divide-y divide-sage-400/50 border-t border-sage-400/50" data-testid="resource-list">
            {filtered.map((r) => (
              <li key={r.id} className="py-7" data-testid={`resource-${r.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="text-2xl">{r.title}</h2>
                  <span className="text-sm text-ink-500">{r.category}</span>
                </div>
                <p className="mt-2 max-w-[65ch]">{r.description}</p>
                {r.url && (
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="link mt-3 inline-flex items-center gap-1.5" data-testid={`resource-link-${r.id}`}>
                    Open {r.title} <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </a>
                )}
              </li>
            ))}
            {filtered.length === 0 && <li className="py-7 text-ink-500" data-testid="resource-empty">No resources match. Try a different word or category.</li>}
          </ul>
          {providers.length > 0 && <TrustedProviders block={blocks?.["resources.trusted_providers"]} providers={providers} />}
        </div>
        <aside className="lg:col-span-4">
          {safety && (
            <div className="surface p-6" data-testid="browsing-safely">
              <h2 className="text-xl">{safety.title}</h2>
              <div className="mt-2 space-y-3 text-sm text-ink-900">{safety.body.split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}</div>
            </div>
          )}
        </aside>
      </div>
    </Page>
  );
}
